import { computed, unref } from 'vue'
import { i18nObject } from 'boot/i18n'
import bs58check from 'bs58check'
import { getConfigs } from 'core/config/index.js'
import { useMainStore } from 'src/stores/main'
import { useSiteInfoStore } from 'src/stores/site-info'
import { useSystemStore } from 'src/stores/system'
import { useTelegramStore } from 'src/stores/telegram'
import { BASE_URL, AVAILABLE_LANG_LIST } from 'src/common/config'
import { tokenService } from 'src/common/token-service'
import { consoleLogAPI } from 'src/common/utils/errors'
import {
  UZBEK_CYRILLIC_LOCALE,
  UZBEK_LATIN_LOCALE,
  getAvailableBrowserLocale,
  getLocaleFromPath,
  normalizeLocale,
} from 'src/common/utils/locale'
import { getCurrencyByRegion } from 'src/common/utils/regions'
import { getResultQuery } from 'src/common/utils/url'
import { getConfig } from 'src/configs'
import { notification } from 'src/core/hooks/notifications'
import { getAuthoritativeStoredLocale, resolveRuntimeLocale } from 'src/core/locale/runtime'
import getAuthLink from 'src/apollo/queries/getAuthLink.gql'
import siteInfoMirrors from 'src/apollo/queries/siteInfoMirrors.gql'

export {
  captureCustomEvent,
  checkContextErrors,
  consoleLogAPI,
  loggingWebview,
  parseApiError,
  sendDebugInfoToSentry,
  showErrorNotification,
} from 'src/common/utils/errors'
export {
  formatMoneyInText,
  getCurrencySymbol,
  getGlyphCurrency,
  getOlympFontCurrencySymbol,
  numberFilter,
  toFixed,
} from 'src/common/utils/number-format'
export {
  actionToken,
  configurationCookiesConfig,
  getAxiosOptionsURL,
  getGqlURL,
  removeUssIdFromCookies,
  setUssidToCookies,
} from 'src/common/utils/auth-session'
export { clearQueryAfterAuth, clearTrailingSlash, getResultQuery, itemIsExist } from 'src/common/utils/url'
export { notEmptyValue } from 'src/common/utils/value'

const config = getConfig()
const coreConfig = getConfigs()

// Legacy compatibility barrel.
// New helpers should go into src/common/utils/* by domain.
export const isWebView = process.env.WEBVIEW === '1'

export function useModalContentTransition(contentTransition, mediaQueries, options = {}) {
  const telegramStore = useTelegramStore()
  const { useResponsiveDefault = true } = options

  return computed(() => {
    if (telegramStore.isTelegramMiniApp) {
      return 'vfm-fade'
    }

    const transition = unref(contentTransition)

    if (transition) {
      return transition
    }

    if (!useResponsiveDefault) {
      return undefined
    }

    return mediaQueries?.mobile ? 'vfm-slide-down' : 'vfm-fade'
  })
}

export function toggleOverflowBody(value) {
  if (value) {
    document.body.classList.add('no-scroll')
  } else {
    document.body.classList.remove('no-scroll')
  }
}

export const dateToString = (date) => {
  let year = date.getFullYear()
  let month = String(date.getMonth() + 1).padStart(2, '0')
  let day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const dateFilter = (value, format = 'dateshort', locale = 'ru-ru') => {
  if (!locale) {
    locale = 'ru-RU'
  }
  if (!value) {
    return ''
  }

  const options = {}
  if (format.includes('datewithoutsecond')) {
    options.day = '2-digit'
    options.month = '2-digit'
    options.year = 'numeric'
    options.hour = '2-digit'
    options.minute = '2-digit'
  } else if (format.includes('datetimeshort')) {
    options.day = '2-digit'
    options.month = '2-digit'
    options.year = 'numeric'
    options.hour = '2-digit'
    options.minute = '2-digit'
    options.second = '2-digit'
  } else if (format.includes('dateshort')) {
    options.day = '2-digit'
    options.month = '2-digit'
    options.year = 'numeric'
  } else if (format.includes('date')) {
    options.day = '2-digit'
    options.month = 'long'
    options.year = 'numeric'
  }
  if (format.includes('time')) {
    options.hour = '2-digit'
    options.minute = '2-digit'
    options.second = '2-digit'
  }
  try {
    return new Intl.DateTimeFormat(locale, options).format(new Date(value))
  } catch {
    console.error('Invalid date format: ', value)
    return ''
  }
}

export function capitalizeFirstLetter(string) {
  if (string && string.length > 0) {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }
  return string
}

export function testChunkFailed(text) {
  const value = String(text?.message || text || '')
  const patterns = [
    /ChunkLoadError:.*Loading chunk/i,
    /Failed to fetch dynamically imported module/i,
    /Importing a module script failed/i,
    /Load failed/i,
  ]
  return patterns.some((pattern) => pattern.test(value))
}

export function testRouteComponentResolveFailed(text) {
  const value = String(text?.message || text || '')
  return /Couldn't resolve component "default" at/i.test(value)
}

export function notifyIfChunkError(error) {
  if (testChunkFailed(error) || testRouteComponentResolveFailed(error)) {
    if (useSystemStore().countOfErrorsChunkLoaded === 0) {
      const actions = [
        {
          label: i18nObject.t('refresh'),
          variant: 'secondary',
          handler: () => {
            location.reload()
          },
        },
      ]
      notification({ type: 'warning', timeout: 15000, text: i18nObject.t('newVersionReloadPage'), actions })
    }
    useSystemStore().updateErrorChunks()
    return true
  }
  return false
}

export function getAvailableLangFromUA(languages, language) {
  if (!languages && !language) {
    return null
  }

  return getAvailableBrowserLocale(languages, language, AVAILABLE_LANG_LIST) || undefined
}

export const defaultLang = process.env.DEFAULT_LANG || 'en'

export function getRedirectLocale() {
  return resolveRuntimeLocale({
    stage: 'application',
    fallbackOnly: true,
    region: useSiteInfoStore().getRealRegion,
  })
}

export function getLocale() {
  return getLang(getLocaleFromPath(location.pathname, BASE_URL, AVAILABLE_LANG_LIST))
}

export function getLang(langFromURL) {
  return resolveRuntimeLocale({
    stage: 'application',
    urlLocale: langFromURL || null,
    region: useSiteInfoStore().getRealRegion,
  })
}

function toCamel(string) {
  return string.replace(/([-_][a-z])/gi, ($1) => $1.toUpperCase().replace('-', '').replace('_', ''))
}

function isObject(args) {
  return args === Object(args) && !Array.isArray(args) && typeof args !== 'function'
}

export const renameKeys = (args) => {
  if (isObject(args)) {
    const n = {}

    Object.keys(args).forEach((k) => {
      n[toCamel(k)] = renameKeys(args[k])
    })

    return n
  }
  if (Array.isArray(args)) {
    return args.map((i) => renameKeys(i))
  }

  return args
}

export const partitionTwoArrayByFilter = (array, filter) => {
  let one = [],
    two = []
  array.forEach((e, idx, arr) => (filter(e, idx, arr) ? one : two).push(e))
  return [one, two]
}

export const getAppToken = (clear = false) => {
  if (!config.app.useToken) {
    return { token: '', type: null, mb_uuid: null, app_id: null }
  }

  const token = useMainStore().getAssid
  const type = useMainStore().getAppType
  const mb_uuid = useMainStore().getMbUuid
  const app_id = useMainStore().getAppId

  if (clear) {
    useMainStore().clear()
  }

  return { token, type, mb_uuid, app_id }
}

export function hasWhiteSpace(s) {
  return /\s/g.test(s)
}

export const withoutCyrillic = (value) => !/\p{Script=Cyrl}/u.test(value)

export const preloaderVisible = (val) => {
  const className = 'ready'
  const preLoaderClassName = '.main-preloader'
  const body = document.body

  const preloaderEl = document.querySelector(preLoaderClassName)

  if (!val) {
    if (preloaderEl) {
      preloaderEl.remove()
    }
    if (!body.classList.contains(className)) {
      body.classList.add(className)
    }
  } else {
    body.classList.remove(className)
  }
}

export function defineSportNameBySportId(sportId) {
  switch (sportId) {
    case 1:
      return 'football'
    case 2:
      return 'hockey'
    case 3:
      return 'tennis'
    case 5:
      return 'basketball'
    case 9:
      return 'handball'
    case 10:
      return 'volleyball'
    default:
      return null
  }
}

export function resolveImagePath(path) {
  try {
    return new URL(path, import.meta.url).href
  } catch (error) {
    console.error(`Error resolving image path: ${path}`, error)
    return ''
  }
}

function clearQuery(query) {
  const list = ['token', 'ussid', 'social']
  const tmp = { ...query }
  list.forEach((p) => delete tmp[p])
  return tmp
}

export const openNewMirror = async (mirror, apollo, query = {}) => {
  const { data: authData } = await apollo.query({
    query: getAuthLink,
    fetchPolicy: 'network-only',
  })

  const { uuid, uuidSecond, hashStr } = authData.authLink
  const queryPath = getResultQuery({ ...clearQuery(query), hashstr: hashStr })
  const linkPath = `/accounts/login/${uuid}/${uuidSecond}${queryPath ? '?' + queryPath : ''}`

  await tokenService.removeToken()
  sessionStorage.removeItem('linkAfterAuth')
  sessionStorage.removeItem(coreConfig.CDA_REDIRECT_KEY)
  localStorage.removeItem(coreConfig.CDA_KEY)

  window.location.replace('https://' + mirror + linkPath)
}

export function simpleRedirect(query) {
  let path = null

  if (query?.path) {
    path = decodeURIComponent(query.path)
  }

  sessionStorage.removeItem('linkAfterAuth')

  const urlStr = path && path.startsWith('/') ? `${location.origin}${BASE_URL}${path}` : `${location.origin}${BASE_URL}`
  const fullUrl = new URL(urlStr)

  const queryToPreserve = { ...query }
  delete queryToPreserve.path

  const searchParams = new URLSearchParams(fullUrl.search)
  for (const [key, value] of Object.entries(queryToPreserve)) {
    if (value !== undefined && value !== null && !searchParams.has(key)) {
      searchParams.append(key, value.toString())
    }
  }

  fullUrl.search = searchParams.toString()

  location.href = fullUrl.toString()
}

export async function checkMirrorAndRedirect({ apolloClient, apolloClientGo, query = {} }) {
  if (isWebView || process.env.DEV || process.env.ENVIRONMENT === 'development' || !config.useMirrors) {
    return simpleRedirect(query)
  }

  return new Promise(async (resolve) => {
    try {
      const { data } = await apolloClientGo.query({
        query: siteInfoMirrors,
        fetchPolicy: 'network-only',
      })

      const { domain, mirrorDomain } = data.siteInfo
      if (!mirrorDomain || domain === mirrorDomain) {
        return simpleRedirect(query)
      }

      await openNewMirror(mirrorDomain, apolloClient, query)
    } catch (e) {
      consoleLogAPI(e)
      simpleRedirect(query)
    } finally {
      resolve()
    }
  })
}

const FINGERPRINT_STORAGE_KEY = 'fp_k'
let fingerprintAgentPromise = null

async function getFingerprintAgent() {
  if (!fingerprintAgentPromise) {
    fingerprintAgentPromise = import('@fingerprintjs/fingerprintjs')
      .then(({ default: FingerprintJS }) => FingerprintJS.load())
      .catch((error) => {
        fingerprintAgentPromise = null
        throw error
      })
  }

  return fingerprintAgentPromise
}

export function prewarmFingerprint() {
  try {
    if (localStorage.getItem(FINGERPRINT_STORAGE_KEY)) {
      return
    }

    void getFingerprintAgent().catch((error) => {
      console.error(error)
    })
  } catch (error) {
    console.error(error)
  }
}

export async function getFingerprint() {
  try {
    if (localStorage.getItem(FINGERPRINT_STORAGE_KEY)) {
      return localStorage.getItem(FINGERPRINT_STORAGE_KEY)
    }

    const fingerprintAgent = await getFingerprintAgent()
    const result = await fingerprintAgent.get()

    localStorage.setItem(FINGERPRINT_STORAGE_KEY, result.visitorId)
    return result.visitorId
  } catch (e) {
    console.error(e)
    return null
  }
}

export function getNewRouteByLang(to, router, redirectLocale = null) {
  //TODO: продумать логику чтобы рут локация работала только с DEFAULT_LANG
  const lang =
    getAuthoritativeStoredLocale() || normalizeLocale(redirectLocale, AVAILABLE_LANG_LIST) || getLang(to.params.locale)
  const localeInUrl = lang?.toLowerCase()
  const isDefaultLang = lang === defaultLang

  if (to.params.locale !== localeInUrl || isDefaultLang) {
    const currentRoute = { ...to }
    currentRoute.params = { ...currentRoute.params, locale: localeInUrl }

    if (isDefaultLang) {
      if (useSystemStore().alt && useSystemStore().isBot) {
        return null
      }

      delete currentRoute.params.locale
    }

    try {
      return router.resolve(currentRoute)
    } catch (e) {
      console.error(e)
      return null
    }
  }
  return null
}

export const getCurrencyByCountry = (countryCode, defaultCountryCode = 'KZT') => {
  return getCurrencyByRegion(countryCode, defaultCountryCode)
}

export const clearSlugAtCDA = async () => {
  return new Promise((resolve) => {
    const image = document.createElement('img')
    image.setAttribute('src', `${process.env.VUE_APP_CDA_URL}slug/?eat_slug=true&url=${location.href}`)
    image.setAttribute('style', 'position: absolute; left: -9999px; opacity: 0')
    image.classList.add('cda-img')

    image.addEventListener('error', function () {
      resolve()
      document.querySelector('img.cda-img').remove()
    })
    image.addEventListener('load', function () {
      resolve()
      document.querySelector('img.cda-img').remove()
    })

    document.body.appendChild(image)
  })
}

export const fillPromoItems = (items, types) => {
  let result = []
  if (items?.length && types) {
    result = items
      .map((promotionItem) => {
        const parsedProps = JSON.parse(promotionItem.props)
        const promotionTypeProps = types.find((i) => +i.id === +parsedProps.promotionTypeId)

        return {
          id: promotionItem.id,
          activeSettings: {
            ...promotionItem.activeSettings,
          },
          images: promotionItem.images,
          props: {
            ...parsedProps,
          },
          promotionTypeProps,
        }
      })
      .filter((promotionItem) => promotionItem.props.active)
      .sort((a, b) => a.props.order - b.props.order)
  }
  return result
}

export const getImageUrl = (function () {
  const images = import.meta.glob('/src/assets/**/*.{png,jpg,svg,webp,gif}', {
    eager: true,
    query: '?url',
    import: 'default',
  })

  const aliases = {
    assets: '/src/assets',
    '@': '/src',
    src: '/src',
  }

  const resolveAlias = (pathWithAlias) => {
    for (const [alias, basePath] of Object.entries(aliases)) {
      if (pathWithAlias.startsWith(alias)) {
        return pathWithAlias.replace(alias, basePath)
      }
    }
    return pathWithAlias
  }

  return function (path) {
    const resolvedPath = resolveAlias(path)

    if (images[resolvedPath]) {
      return images[resolvedPath]
    }

    const fallbackPath = resolvedPath.replaceAll(UZBEK_CYRILLIC_LOCALE, UZBEK_LATIN_LOCALE)
    if (fallbackPath !== resolvedPath && images[fallbackPath]) {
      return images[fallbackPath]
    }

    console.error(`Image ${path} not found.`)
    return ''
  }
})()

export function getSocialLinksByRegion(region) {
  const socialLinks = config.socialLinks

  if (region) {
    const formattedRegion = region.toLowerCase().trim()
    return { ...socialLinks.default, ...(socialLinks.region?.[formattedRegion] ?? {}) }
  }

  return socialLinks.default
}

// TODO: Сделано в рамках dev-4140, по-возможности придумать другое решение
export function makeTableScrollable() {
  const observer = new MutationObserver(() => {
    const tables = document.querySelectorAll('table')
    if (tables.length === 0) {
      return
    }

    tables.forEach((table) => {
      if (table.parentElement?.classList.contains('table-wrap')) {
        return
      }

      const wrapper = document.createElement('div')
      wrapper.className = 'table-wrap'
      table.replaceWith(wrapper)
      wrapper.appendChild(table)
    })

    observer.disconnect()
  })

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  })
}

const accumulateSettings = {
  USD: { start: -2, steps: [0.05, 0.12, 0.2], isFloat: true },
  UZS: { start: -500, steps: [5, 9, 14], isFloat: false },
  UAH: { start: -100, steps: [2, 4, 6], isFloat: false },
  KZT: { start: -100, steps: [2, 4, 6], isFloat: false },
  KGS: { start: -100, steps: [1, 2, 3], isFloat: false },
  RUB: { start: -50, steps: [1, 2, 3], isFloat: false },
  BYN: { start: -50, steps: [1, 2, 3], isFloat: false },
  MDL: { start: -50, steps: [1, 2, 3], isFloat: false },
  BDT: { start: -50, steps: [1, 2, 3], isFloat: false },
  AZN: { start: -2, steps: [0.05, 0.12, 0.2], isFloat: true },
  EUR: { start: -2, steps: [0.05, 0.12, 0.2], isFloat: true },
}

export function accumulateJackpotLevel(level, code) {
  const settings = accumulateSettings[code]

  if (!settings || !level?.amount || level.amount?.value === undefined) {
    console.warn(`⚠️ No settings found for currency code "${code}"`)
    return
  }

  const originalAmount = level?.amount?.value

  // Применяем стартовое смещение

  if (settings.isFloat) {
    level.amount.value = parseFloat((level?.amount?.value + settings.start).toFixed(2))
  } else {
    level.amount.value += settings.start
  }
  if (level.amount.value + settings.start < 0) {
    level.amount.value = 0
  }

  let stepIndex = 0
  let timeoutId

  const tick = () => {
    const step = settings.steps[stepIndex]

    if (settings.isFloat) {
      level.amount.value = parseFloat((level.amount?.value + step).toFixed(2))
    } else {
      level.amount.value += step
    }

    stepIndex = (stepIndex + 1) % settings.steps.length

    if (level.amount.value >= originalAmount) {
      level.amount.value = originalAmount
      return // остановка
    }

    // Случайный интервал: либо 2000, либо 3000 мс
    const delay = Math.random() < 0.5 ? 2000 : 3000
    timeoutId = setTimeout(tick, delay)
  }

  // Запускаем первый шаг
  timeoutId = setTimeout(tick, Math.random() < 0.5 ? 2000 : 3000)

  // Возвращаем ID таймера, чтобы можно было отменить при необходимости
  return () => clearTimeout(timeoutId)
}

export function normalizeGqlResponseList(data) {
  if (!data) {
    return { list: [], count: 0 }
  }

  return {
    list: data.edges.map((e) => e.node),
    count: data.totalCount,
  }
}

export function isValidCryptoAddress(address, version) {
  try {
    const payload = bs58check.decode(address) // Buffer
    return payload[0] === version
  } catch {
    return false
  }
}

export function lightenHex(hex, factor) {
  hex = hex.replace(/^#/, '')
  let r = parseInt(hex.substring(0, 2), 16)
  let g = parseInt(hex.substring(2, 4), 16)
  let b = parseInt(hex.substring(4, 6), 16)

  r = Math.min(255, Math.round(r * factor))
  g = Math.min(255, Math.round(g * factor))
  b = Math.min(255, Math.round(b * factor))

  return (
    '#' +
    r.toString(16).padStart(2, '0') +
    g.toString(16).padStart(2, '0') +
    b.toString(16).padStart(2, '0')
  ).toUpperCase()
}

export function normalizeHost(host) {
  return host.replace(/^www\./, '')
}

export function setShowAppData(value) {
  const data = {
    value,
    expiry: Date.now() + 30 * 24 * 60 * 60 * 1000, // 30 дней
  }
  localStorage.setItem('showapp', JSON.stringify(data))
}

export function getShowAppData() {
  const raw = localStorage.getItem('showapp')
  if (!raw) {
    return null
  }
  try {
    const parsed = JSON.parse(raw)
    if (parsed.expiry > Date.now()) {
      return parsed.value
    } else {
      localStorage.removeItem('showapp')
      return null
    }
  } catch {
    localStorage.removeItem('showapp')
    return null
  }
}

export function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}
export function safeJsonParse(value) {
  try {
    return JSON.parse(value || '{}')
  } catch (err) {
    console.error('props parse error', err)
    return {}
  }
}

export function resolveId(value) {
  if (typeof value === 'number') {
    return value
  }
  if (value && typeof value === 'object') {
    return value.id ?? null
  }
  return null
}
